// Logique PURE de la GRANDE RÉVÉLATION de la Mine (REFONTE 19/07) : minutage des phases + enveloppes
// cosmétiques (montée / assombrissement / flottement / rotation des rayons / flash) et couleurs par
// rareté. ZÉRO dépendance DOM/canvas/audio -> testable en node (tests/mine-crack). Le contrôleur
// (mine.ts) lit ces valeurs par frame sur l'horloge murale (performance.now) et rend en conséquence.
// Purement cosmétique : jamais dans la simulation, le hash ou le rejeu réseau.

export type Rarity = 'courant' | 'rare' | 'epique' | 'mega'
export type PickaxeVariant = 'classic' | 'marteau' | 'foreuse'

// --- Minutage (ms depuis le début de la révélation). Bornes en TEMPS (jamais en compteur de frames
// fragile) : une image sautée ne décale rien, la carte tombe TOUJOURS à CARD_MS via un setTimeout.
export const GROW_MS = 620   // le loot grandit depuis la case creusée jusqu'au centre (ease-out)
export const CARD_MS = 820   // fanfare + carte DOM détaillée (échéance DURE) — juste après la montée

// Échelle d'apparition : 0 (dans la case creusée) -> 1 (taille pleine au centre), ease-out cubique,
// maintenue à 1 ensuite. Pilote À LA FOIS la taille du sprite ET l'interpolation de sa position.
export function revealScale(t: number): number {
  const k = Math.max(0, Math.min(1, t / GROW_MS))
  return 1 - Math.pow(1 - k, 3) // ease-out cubique (« pop » net en fin de montée)
}

// Assombrissement radial de la scène : 0 -> DARKEN_MAX sur ~80 % de la montée, puis maintenu quasi
// noir (le regard reste sur le loot). Cosmétique pur.
export const DARKEN_MAX = 0.9
export function revealDarken(t: number): number {
  const k = Math.max(0, Math.min(1, t / (GROW_MS * 0.8)))
  return DARKEN_MAX * k
}

// Flash blanc bref au « pop » (fin de la montée) : nul avant GROW_MS, pic à GROW_MS, éteint après
// FLASH_FADE_MS. Cosmétique pur.
export const FLASH_PEAK = 0.6
export const FLASH_FADE_MS = 260
export function revealFlash(t: number): number {
  const d = t - GROW_MS
  if (d < 0 || d >= FLASH_FADE_MS) return 0
  return FLASH_PEAK * (1 - d / FLASH_FADE_MS)
}

// Flottement vertical (sinus doux) du loot présenté, en px (amplitude par défaut 10).
export function floatY(t: number, amp = 10): number {
  return Math.sin(t / 620) * amp
}
// Léger balancement (rotation, radians) du loot présenté.
export function revealSway(t: number): number {
  return Math.sin(t / 900) * 0.07
}
// Rotation LENTE des rayons de lumière (radians) : monotone croissante, cosmétique pur.
export function raySpin(t: number): number {
  return t / 2600
}

// --- Couleurs par rareté ---------------------------------------------------------------------------
// Éclats/particules (courant blanc-gris / rare bleu / épique violet / méga or+arc-en-ciel). méga
// renvoie PLUSIEURS teintes -> l'émetteur pioche en boucle (effet arc-en-ciel doré).
const RAINBOW = ['#ffd23f', '#ff5f57', '#5db4ff', '#7cfc7c', '#b98bff', '#ff9a3d']
export function rarityShards(rarity: Rarity): string[] {
  switch (rarity) {
    case 'rare': return ['#5db4ff', '#8fd0ff', '#cbe8ff']
    case 'epique': return ['#b98bff', '#d3b8ff', '#8f5bff']
    case 'mega': return RAINBOW
    case 'courant':
    default: return ['#e8e4f2', '#c9c4de', '#ffffff']
  }
}
// Teinte DOMINANTE d'une rareté (halo, anneaux de halo, bandeau) : une seule couleur.
export function rarityColor(rarity: Rarity): string {
  switch (rarity) {
    case 'rare': return '#5db4ff'
    case 'epique': return '#b98bff'
    case 'mega': return '#ffd23f'
    case 'courant':
    default: return '#c9c4de'
  }
}
// Couleurs des RAYONS de lumière : méga = arc-en-ciel (plusieurs teintes), sinon la dominante seule.
export function rarityRays(rarity: Rarity): string[] {
  return rarity === 'mega' ? RAINBOW : [rarityColor(rarity)]
}
export function isMega(rarity: Rarity): boolean {
  return rarity === 'mega'
}

// Variante de pioche d'après le loot équipé au slot `pickaxe` : sert au SON d'impact de la casse
// (marteau = frappe plus lourde). Pure -> testable.
export function pickaxeVariantOf(equippedPickaxe: string | undefined): PickaxeVariant {
  return equippedPickaxe === 'pioche-marteau' ? 'marteau'
    : equippedPickaxe === 'pioche-foreuse' ? 'foreuse'
      : 'classic'
}
